import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { AppShell } from '@components/layout'

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-body-sm text-text-muted">Loading...</p>
      </div>
    </div>
  )
}

// Suspense wrapper for lazy-loaded components
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

// Core Pages - Lazy loaded
const Dashboard = lazy(() =>
  import('@pages/Dashboard').then((m) => ({ default: m.Dashboard }))
)
const BusinessList = lazy(() =>
  import('@pages/Businesses').then((m) => ({ default: m.BusinessList }))
)
const BusinessDetail = lazy(() =>
  import('@pages/Businesses').then((m) => ({ default: m.BusinessDetail }))
)
const KeywordManagement = lazy(() =>
  import('@pages/Keywords').then((m) => ({ default: m.KeywordManagement }))
)

// Secondary Features - Lazy loaded
const InsightsFeed = lazy(() =>
  import('@pages/Insights').then((m) => ({ default: m.InsightsFeed }))
)
const NotificationsFeed = lazy(() =>
  import('@pages/Notifications').then((m) => ({ default: m.NotificationsFeed }))
)
const ReportBuilder = lazy(() =>
  import('@pages/Reports').then((m) => ({ default: m.ReportBuilder }))
)
const ConsultationsPage = lazy(() =>
  import('@pages/Consultations').then((m) => ({ default: m.ConsultationsPage }))
)
const SettingsPage = lazy(() =>
  import('@pages/Settings').then((m) => ({ default: m.SettingsPage }))
)

// Auth Pages - Lazy loaded
const LoginPage = lazy(() =>
  import('@pages/Auth').then((m) => ({ default: m.LoginPage }))
)
const RegisterPage = lazy(() =>
  import('@pages/Auth').then((m) => ({ default: m.RegisterPage }))
)
const ForgotPasswordPage = lazy(() =>
  import('@pages/Auth').then((m) => ({ default: m.ForgotPasswordPage }))
)
const ResetPasswordPage = lazy(() =>
  import('@pages/Auth').then((m) => ({ default: m.ResetPasswordPage }))
)
const EmailVerificationPage = lazy(() =>
  import('@pages/Auth').then((m) => ({ default: m.EmailVerificationPage }))
)

/**
 * Application router configuration with code splitting
 * Each route is lazy-loaded for optimal bundle size
 */
export const router = createBrowserRouter([
  // Auth routes (no shell)
  {
    path: '/auth/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/register',
    element: (
      <SuspenseWrapper>
        <RegisterPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/forgot-password',
    element: (
      <SuspenseWrapper>
        <ForgotPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/verify-email',
    element: (
      <SuspenseWrapper>
        <EmailVerificationPage />
      </SuspenseWrapper>
    ),
  },

  // Main app routes (with shell)
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <SuspenseWrapper>
            <Dashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'businesses',
        element: (
          <SuspenseWrapper>
            <BusinessList />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'businesses/:id',
        element: (
          <SuspenseWrapper>
            <BusinessDetail />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'keywords',
        element: (
          <SuspenseWrapper>
            <KeywordManagement />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'insights',
        element: (
          <SuspenseWrapper>
            <InsightsFeed />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'notifications',
        element: (
          <SuspenseWrapper>
            <NotificationsFeed />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports',
        element: (
          <SuspenseWrapper>
            <ReportBuilder />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'consultations',
        element: (
          <SuspenseWrapper>
            <ConsultationsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings',
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
])
